/**
 * feat-301-server-multiple-roles — the Add/Edit Server modal's Roles fieldset.
 *
 * Design: worktrees/feat-301-server-multiple-roles/design/server-roles-form.html
 * (panels R1–R10) and design/NOTES.md §1 (selection rules table).
 *
 * Controlled: the parent owns `roles` (the checked set) and `primary`
 * (`default_role`). The only local state is the R7 "Primary moved" status
 * line, which clears on the next interaction.
 *
 * The Primary radio exists ONLY on checked rows (absent, not disabled), so a
 * primary outside the set cannot be built in the UI. The API's 422 is the
 * backstop.
 */
import React, { useState } from "react";
import { TenantRole } from "../../types/prompt";
import { SERVER_ROLES_COPY } from "./serverRoles";

export type TenantRolesStatus = "loading" | "loaded" | "error";

export interface RoleSelection {
  roles: string[];
  primary: string;
}

/**
 * Pure transition for checking/unchecking a role (design NOTES §1 table).
 * `listOrder` is the tenant's role list order (the API's ORDER BY role_key).
 * Returns the next selection and, when the primary moved automatically, the
 * role_key it moved to.
 */
export function toggleRole(
  current: RoleSelection,
  key: string,
  checked: boolean,
  listOrder: string[],
): { next: RoleSelection; autoMovedTo: string | null } {
  if (checked) {
    if (current.roles.includes(key)) return { next: current, autoMovedTo: null };
    const roles = [...current.roles, key];
    const primary = current.primary && roles.includes(current.primary) ? current.primary : key;
    return { next: { roles, primary }, autoMovedTo: null };
  }
  const roles = current.roles.filter((r) => r !== key);
  if (current.primary !== key) {
    return { next: { roles, primary: current.primary }, autoMovedTo: null };
  }
  // Unchecked the primary: first remaining checked role in LIST order.
  const inList = listOrder.find((k) => roles.includes(k));
  const primary = inList ?? roles[0] ?? "";
  return { next: { roles, primary }, autoMovedTo: primary || null };
}

/**
 * The create/update payload fragment for roles (ADR 0001 §6.1).
 *
 * - Roles not loaded (failed, or still loading): `{}` — BOTH `roles` and
 *   `default_role` omitted, which the backend treats as "roles untouched"
 *   (§6.1 row 1; on create: no roles). Never `roles: []` because a fetch failed.
 * - Loaded: `roles` primary first, then the other checked roles in list order;
 *   `default_role` = primary, or `""` with no roles.
 */
export function rolesPayload(
  selection: RoleSelection,
  status: TenantRolesStatus,
  listOrder: string[],
): { roles?: string[]; default_role?: string } {
  if (status !== "loaded") return {};
  const { roles } = selection;
  if (roles.length === 0) return { roles: [], default_role: "" };
  const primary = selection.primary && roles.includes(selection.primary)
    ? selection.primary
    : (listOrder.find((k) => roles.includes(k)) ?? roles[0]);
  const rank = (k: string) => {
    const i = listOrder.indexOf(k);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const others = roles.filter((r) => r !== primary).sort((a, b) => rank(a) - rank(b));
  return { roles: [primary, ...others], default_role: primary };
}

interface ServerRolesFieldProps {
  tenantRoles: TenantRole[];
  status: TenantRolesStatus;
  roles: string[];
  primary: string;
  onChange: (next: RoleSelection) => void;
}

const HELP_ID = "server-roles-help";

export const ServerRolesField: React.FC<ServerRolesFieldProps> = ({
  tenantRoles,
  status,
  roles,
  primary,
  onChange,
}) => {
  const [movedTo, setMovedTo] = useState<string | null>(null);
  const listOrder = tenantRoles.map((r) => r.role_key);
  const legend = <legend className="block text-gray-700 mb-2">{SERVER_ROLES_COPY.legend}</legend>;

  if (status === "loading") {
    return (
      <fieldset className="mb-4" aria-busy="true" data-testid="server-roles-fieldset">
        {legend}
        <div className="h-9 rounded bg-gray-100 animate-pulse mb-2" />
        <div className="h-9 rounded bg-gray-100 animate-pulse mb-2" />
        <p className="text-sm text-gray-500">{SERVER_ROLES_COPY.loading}</p>
      </fieldset>
    );
  }

  if (status === "error") {
    return (
      <fieldset className="mb-4" data-testid="server-roles-fieldset">
        {legend}
        <div
          className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
          data-testid="server-roles-load-error"
        >
          {SERVER_ROLES_COPY.loadFailed(roles)}
        </div>
      </fieldset>
    );
  }

  if (tenantRoles.length === 0) {
    return (
      <fieldset className="mb-4" data-testid="server-roles-fieldset">
        {legend}
        <p className="text-sm text-gray-500">{SERVER_ROLES_COPY.tenantHasNoRoles}</p>
      </fieldset>
    );
  }

  const displayName = (key: string) =>
    tenantRoles.find((r) => r.role_key === key)?.display_name ?? key;

  const handleCheck = (key: string, checked: boolean) => {
    const { next, autoMovedTo } = toggleRole({ roles, primary }, key, checked, listOrder);
    setMovedTo(autoMovedTo);
    onChange(next);
  };

  const handlePrimary = (key: string) => {
    setMovedTo(null);
    onChange({ roles, primary: key });
  };

  return (
    <fieldset className="mb-4" aria-describedby={HELP_ID} data-testid="server-roles-fieldset">
      {legend}
      {tenantRoles.map((role) => {
        const key = role.role_key;
        const isChecked = roles.includes(key);
        return (
          <div
            key={role.id}
            className={`flex items-center justify-between gap-3 border rounded px-3 py-2 mb-2 text-sm ${
              isChecked ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white"
            }`}
            data-testid={`server-role-row-${key}`}
          >
            <label className="flex items-center gap-2 min-w-0 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 flex-shrink-0"
                checked={isChecked}
                onChange={(e) => handleCheck(key, e.target.checked)}
                data-testid={`server-role-checkbox-${key}`}
              />
              <span className="text-gray-700 min-w-0 break-words">{role.display_name}</span>
            </label>
            {isChecked && (
              <label className="flex items-center gap-1.5 flex-shrink-0 text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="primary_role"
                  className="h-4 w-4"
                  checked={primary === key}
                  onChange={() => handlePrimary(key)}
                  aria-label={`${SERVER_ROLES_COPY.primary} (${role.display_name})`}
                  data-testid={`server-role-primary-${key}`}
                />
                {` ${SERVER_ROLES_COPY.primary}`}
              </label>
            )}
          </div>
        );
      })}
      {roles.length === 0 && (
        <p className="text-sm text-gray-500 mb-1">{SERVER_ROLES_COPY.noneSelected}</p>
      )}
      {movedTo && (
        <p role="status" data-testid="server-roles-status" className="text-xs text-blue-700 mb-1">
          {SERVER_ROLES_COPY.primaryMoved(displayName(movedTo))}
        </p>
      )}
      <p id={HELP_ID} className="text-xs text-gray-400 mt-1">
        {SERVER_ROLES_COPY.helper}
      </p>
    </fieldset>
  );
};
